import { createFileRoute } from "@tanstack/react-router";
import { Link, Navigate, Outlet, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useAdminSession } from "@/lib/admin/rbac";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin Console — CB16" },
      {
        name: "description",
        content: "CB16 administration console for orders, customers, catalog and content.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Admin Console — CB16" },
      { property: "og:description", content: "CB16 administration console." },
    ],
  }),
  component: AdminLayout,
});

function AdminLayout() {
  const { isAuthenticated, ready } = useAuth();
  if (!ready) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!isAuthenticated) {
    return <Navigate to="/login" search={{ redirect: "/admin" } as never} replace />;
  }
  return (
    <MinimalAdminShell>
      <Outlet />
    </MinimalAdminShell>
  );
}

function MinimalAdminShell({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const currentTab = useRouterState({
    select: (s) => {
      const t = (s.location.search as Record<string, unknown>)?.tab;
      return typeof t === "string" ? t : "";
    },
  });
  const { user, signOut } = useAuth();
  const isSuperAdmin = useAdminSession().roles.includes("super_admin");
  return (
    <div className="min-h-screen bg-[#F7F6F3] text-black">
      <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 lg:px-10 lg:py-12">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-black/10 pb-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C6A15B]">
              CB16 · Console
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-black sm:text-4xl">
              Operator Console
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {user?.email && (
              <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-black/60">
                {user.email}
              </span>
            )}
            <Link
              to="/"
              className="text-[11px] font-medium uppercase tracking-[0.22em] text-black/60 hover:text-[#C6A15B]"
            >
              ← Store
            </Link>
            <button
              type="button"
              onClick={() => { void signOut(); }}
              className="text-[11px] font-medium uppercase tracking-[0.22em] text-black/60 hover:text-[#C6A15B]"
            >
              Sign out
            </button>
          </div>
        </header>
        <div className="grid gap-8 lg:grid-cols-[240px_1fr] lg:gap-10">
          <aside className="rounded-sm bg-white border border-black/10 p-4">
            <nav aria-label="Admin" className="flex flex-col gap-6">
              {NAV_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-black/50">
                    {group.label}
                  </p>
                  <div className="flex flex-col">
                    {group.items.filter((item) => !item.superAdminOnly || isSuperAdmin).map((item) => {
                      const pathMatch = item.exact
                        ? pathname === item.to
                        : pathname === item.to || pathname.startsWith(item.to + "/");
                      // Tab entries are active only on their tab; the plain entry yields to them.
                      const active = item.tab
                        ? pathMatch && currentTab === item.tab
                        : pathMatch && !(currentTab && group.items.some((i) => i.tab === currentTab && i.to === item.to));
                      return (
                        <Link
                          key={item.to + (item.tab ?? "")}
                          to={item.to}
                          search={(item.tab ? { tab: item.tab } : {}) as never}
                          className={
                            "block border-l-2 py-2 text-[12px] font-semibold tracking-[0.02em] transition " +
                            (item.indent ? "pl-6 pr-3 " : "px-3 ") +
                            (active
                              ? "border-[#C6A15B] bg-[#C6A15B]/5 text-black"
                              : "border-transparent text-black/70 hover:border-black/20 hover:bg-black/[0.03] hover:text-black")
                          }
                        >
                          {item.indent ? "↳ " : ""}{item.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </aside>
          <div>{children}</div>
        </div>
      </div>
    </div>
  );
}

/** `tab` deep-links into a Settings tab via ?tab=…; `indent` shows it as a sub-entry. */
type NavItem = { to: string; label: string; exact?: boolean; superAdminOnly?: boolean; tab?: string; indent?: boolean };
const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  { label: "Overview", items: [
    { to: "/admin", label: "Dashboard", exact: true },
  ]},
  { label: "Analytics", items: [
    { to: "/admin/analytics", label: "Website Analytics", exact: true },
    { to: "/admin/analytics/sales", label: "Sales Analytics" },
    { to: "/admin/analytics/customers", label: "Customer Analytics" },
    { to: "/admin/analytics/inventory", label: "Inventory Reports" },
  ]},
  { label: "Catalog", items: [
    { to: "/admin/products", label: "Products" },
    { to: "/admin/collections", label: "Collections" },
    { to: "/admin/packs", label: "Packs" },
    { to: "/admin/inventory", label: "Inventory" },
  ]},
  { label: "Commerce", items: [
    { to: "/admin/orders", label: "Orders" },
    { to: "/admin/shipping-partners", label: "Shipping Partners" },
    { to: "/admin/quotes", label: "Corporate Quotes" },
    { to: "/admin/customers", label: "Customers" },
    { to: "/admin/enquiries", label: "Customer Enquiries" },
    { to: "/admin/promos", label: "Promo Codes" },
  ]},
  { label: "Content", items: [
    { to: "/admin/cms", label: "CMS Pages" },
    { to: "/admin/blog", label: "Blog Posts" },
    { to: "/admin/blog-taxonomy", label: "Blog Taxonomy" },
    { to: "/admin/faq", label: "FAQ Manager" },
    { to: "/admin/media", label: "Media Library" },
    { to: "/admin/video-gallery", label: "Sales Videos" },
  ]},
  { label: "Marketing", items: [
    { to: "/admin/subscribers", label: "Blog Subscribers" },
    { to: "/admin/campaigns", label: "Email Campaigns" },
    { to: "/admin/social", label: "Social Media Studio" },
    { to: "/admin/ai-social", label: "AI & Social Integrations" },
    { to: "/admin/email-diagnostics", label: "Email Logs" },
    { to: "/admin/ai-chat", label: "AI Chat Settings" },
  ]},
  { label: "Access", items: [
    { to: "/admin/roles", label: "Users & Roles" },
    { to: "/admin/tenants", label: "Businesses", superAdminOnly: true },
    { to: "/admin/audit", label: "Audit Log" },
    { to: "/admin/settings", label: "Settings", exact: true },
    { to: "/admin/settings", label: "Homepage Promotions", tab: "homepage-promotions", indent: true },
  ]},
];
